import { AttributesSelector } from '@workwhile/ui'
import React, { useMemo } from 'react'
import { FormProvider, useFormContext, Path, PathValue } from 'react-hook-form'
import { formatDateRange } from 'lib/formatDateRange'

export interface DateRangeFilterProps<
  FilterSchema extends Record<string, any>,
> {
  dateRangeFieldPath: Path<FilterSchema>
  typeFieldPath?: Path<FilterSchema>
  label: string
  filterKey: string
  children: React.ReactNode
  formatSelectedAttributes?: (dateRange: {
    from: Date | null
    to: Date | null
  }) => string[]
  resetDefaultValues?: {
    dateRange?: { from: Date | null; to: Date | null } | null
    type?: string | null
  }
}

export const DateRangeFilter = <FilterSchema extends Record<string, any>>({
  dateRangeFieldPath,
  typeFieldPath,
  label,
  filterKey,
  children,
  formatSelectedAttributes,
  resetDefaultValues,
}: DateRangeFilterProps<FilterSchema>) => {
  const methods = useFormContext<FilterSchema>()

  const dateRange = methods.watch(dateRangeFieldPath) as {
    from: Date | null
    to: Date | null
  } | null

  const type = typeFieldPath
    ? (methods.watch(typeFieldPath) as string | null)
    : null

  const resetToDefaultValues = () => {
    if (resetDefaultValues) {
      if (resetDefaultValues.dateRange !== undefined) {
        methods.setValue(
          dateRangeFieldPath,
          resetDefaultValues.dateRange as PathValue<
            FilterSchema,
            Path<FilterSchema>
          >
        )
      }
      if (typeFieldPath && resetDefaultValues.type !== undefined) {
        methods.setValue(
          typeFieldPath,
          resetDefaultValues.type as PathValue<FilterSchema, Path<FilterSchema>>
        )
      }
    } else {
      // Default reset behavior
      if (typeFieldPath) {
        methods.resetField(typeFieldPath, {
          defaultValue: null as PathValue<FilterSchema, Path<FilterSchema>>,
        })
      }
      methods.resetField(dateRangeFieldPath, {
        defaultValue: {
          from: null,
          to: null,
        } as PathValue<FilterSchema, Path<FilterSchema>>,
      })
    }
  }

  const selectedAttributes = useMemo(() => {
    if (formatSelectedAttributes && dateRange) {
      return formatSelectedAttributes(dateRange)
    }

    const attributes: string[] = []

    if (type === 'past') {
      attributes.push('Past')
    }

    if (type === 'ongoing') {
      attributes.push('Ongoing')
    }

    if (type === 'upcoming') {
      attributes.push('Upcoming')
    }

    attributes.push(...formatDateRange(dateRange))

    return attributes
  }, [dateRange, type, formatSelectedAttributes])

  return (
    <FormProvider {...methods}>
      <AttributesSelector
        label={label}
        rootGroupTitle="Filter by"
        selectedAttributes={selectedAttributes}
        onClearAll={resetToDefaultValues}
        groups={
          <AttributesSelector.Group
            key={filterKey}
            title={label}
            numberOfSelectedAttributes={selectedAttributes.length}
          >
            {children}
          </AttributesSelector.Group>
        }
      />
    </FormProvider>
  )
}
